import React, { useEffect, useRef } from 'react';

interface ModalProps {
  show?: boolean;
  onHide?: () => void;
  className?: string;
  children?: React.ReactNode;
  role?: string;
  'aria-label'?: string;
}

function ModalBase({ show, onHide, className = '', children, ...rest }: ModalProps) {
  useEffect(() => {
    if (show) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => { document.body.classList.remove('modal-open'); };
  }, [show]);

  if (!show) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onHide?.();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        tabIndex={-1}
        onClick={handleBackdropClick}
        {...rest}
      >
        <div className={`bg-base-100 rounded-lg shadow-xl w-full max-w-lg mx-4 ${className}`.trim()}>
          {children}
        </div>
      </div>
    </>
  );
}

interface SubProps {
  className?: string;
  children?: React.ReactNode;
}

interface HeaderProps extends SubProps {
  closeButton?: boolean;
}

function ModalHeader({ closeButton, className = '', children }: HeaderProps & { closeButton?: boolean }) {
  return (
    <div className={`flex items-center justify-between p-4 border-b border-base-300 ${className}`.trim()}>
      {children}
      {closeButton && (
        <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close"
          onClick={(e) => {
            const modal = e.currentTarget.closest('.fixed.z-50');
            modal?.dispatchEvent(new CustomEvent('modal-close', { bubbles: true }));
          }}>✕</button>
      )}
    </div>
  );
}

function ModalTitle({ className = '', children, ...rest }: SubProps & { role?: string }) {
  return <h5 className={`text-lg font-semibold ${className}`.trim()} {...rest}>{children}</h5>;
}

function ModalBody({ className = '', children }: SubProps) {
  return <div className={`p-4 ${className}`.trim()}>{children}</div>;
}

function ModalFooter({ className = '', children }: SubProps) {
  return <div className={`flex justify-end gap-2 p-4 border-t border-base-300 ${className}`.trim()}>{children}</div>;
}

const Modal = Object.assign(ModalBase, {
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
  Footer: ModalFooter,
});

export default Modal;
